import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * 4-language localisation for the public /tools tools.
 *
 * The tools were built binary (Swedish/English) via an `isSv` boolean and
 * `*Sv`/`*En` field pairs. This helper extends that to French + Spanish without
 * routing thousands of tool strings through the global i18next bundles (which
 * would bloat en/sv and fight the sv↔en parity guard). A tool string is a small
 * `{ sv, en, fr?, es?, de? }` object; `fr`/`es`/`de` fall back to English when
 * absent — exactly the behaviour the tools already had for fr/es users.
 *
 * fi/no also fall back to English here: the tools rendered English for those
 * languages before this layer, so we preserve that (only sv/fr/es/de are
 * special — `de` is now handled for the German /germany landing edition).
 */
export interface Localized {
  sv: string
  en: string
  fr?: string
  es?: string
  de?: string
}

/** Pick the string for `lang` from a `{ sv, en, fr?, es? }` object. */
export function localize(lang: string | undefined, t: Localized): string {
  switch ((lang || 'en').toLowerCase().split('-')[0]) {
    case 'sv':
      return t.sv
    case 'fr':
      return t.fr ?? t.en
    case 'es':
      return t.es ?? t.en
    case 'de':
      return t.de ?? t.en
    default:
      return t.en
  }
}

/**
 * Hook form: returns the active language and an `L` picker bound to it.
 *
 *   const { L } = useLocalize()
 *   <h1>{L({ sv: 'Hej', en: 'Hello', fr: 'Bonjour', es: 'Hola' })}</h1>
 */
export function useLocalize() {
  const { i18n } = useTranslation()
  const lang = i18n.language || 'en'
  // Memoised on lang so `L` (and the object) are referentially stable across
  // renders — components put `L` in useEffect deps, and an unstable `L`
  // re-fires those effects on every render (the InstantRiskCheck fetch loop
  // that OOM'd jsdom and would spam Nominatim in production).
  return useMemo(
    () => ({
      lang,
      isSv: lang.toLowerCase().startsWith('sv'),
      L: (t: Localized) => localize(lang, t),
    }),
    [lang],
  )
}
